import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthGaurdService } from 'src/app/service/auth-gaurd.service';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import { ExportExcelService } from 'src/app/service/export-excel.service';
import { UploadsConfigModel } from 'src/app/models/upload/uploadsconfig.model';
import { UploadsConfigService } from 'src/app/service/uploadsconfig.service';
import { GlobalService } from 'src/app/service/global.service';
import { Subscription } from 'rxjs';
import { extractApiErrorMessage } from '../upload-template.util';

@Component({
  selector: 'app-uploads-edit',
  templateUrl: './uploads-edit.component.html',
  styleUrls: ['../upload-edit-layout.css']
})
export class UploadsEditComponent implements OnInit, OnDestroy {

  public uploadsConfigModel = new UploadsConfigModel();
  public uploadsConfigModel1 = new UploadsConfigModel();
  public columnsList: UploadsConfigModel[] = [];
  public columnsEditList: UploadsConfigModel[];
  public selectedUserDoc: UploadsConfigModel[] = [];
  public loggedinUser = '';
  public myUploadsSet = new Set<string>();
  public activeSqlQuery = '';
  isAccessDisabled = false;
  private subscriptions: Subscription[] = [];
  private sqlByAction: { Insert: string; Update: string } = { Insert: '', Update: '' };

  constructor(
    private globalService: GlobalService,
    private router: Router,
    private route: ActivatedRoute,
    private uploadsConfigService: UploadsConfigService,
    public ete: ExportExcelService,
    private authGaurdService: AuthGaurdService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.isAccessDisabled = this.globalService._permissionCheck('uploads-view');
    this.loggedinUser = this.authGaurdService.loginUser();
    this.uploadsConfigModel = this.uploadsConfigService.uploadsConfigModel;
    this.myUploadsSet = this.uploadsConfigService.myUploadsSet;
    this.initializeFromContext();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeFromContext(): void {
    const params = this.route.snapshot.queryParams;
    const recordTemplate = params['recordtemplate']
      || this.uploadsConfigService.uploadsConfigModel?.recordtemplate;
    const uploadTemplateName = params['uploadtemplatename']
      || this.uploadsConfigService.uploadsConfigModel?.uploadtemplatename;

    if (!recordTemplate || !uploadTemplateName) {
      this.router.navigate(['/uploads-view']);
      return;
    }

    this.uploadsConfigModel1.recordtemplate = recordTemplate;
    this.uploadsConfigModel1.uploadtemplatename = uploadTemplateName;

    const cached = this.uploadsConfigService.uploadsConfigModel;
    if (cached?.recordtemplate === recordTemplate
      && cached?.uploadtemplatename === uploadTemplateName) {
      this.applyHeaderFields(cached);
    }

    if (params['recordtemplate'] !== recordTemplate
      || params['uploadtemplatename'] !== uploadTemplateName) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { recordtemplate: recordTemplate, uploadtemplatename: uploadTemplateName },
        replaceUrl: true
      });
    }

    this.getColumnsEditList();
  }

  private applyHeaderFields(source: UploadsConfigModel): void {
    this.uploadsConfigModel1.uploadtemplatename = source.uploadtemplatename;
    this.uploadsConfigModel1.uploadtemplatedesc = source.uploadtemplatedesc;
    this.uploadsConfigModel1.recordtemplate = source.recordtemplate;
    this.uploadsConfigModel1.actiontype = source.actiontype || 'Insert';
    this.uploadsConfigModel1.transformationroutine = source.transformationroutine;
    if (source.sqlquery) {
      this.cacheSqlForAction(this.uploadsConfigModel1.actiontype, source.sqlquery);
    }
    this.syncActiveSqlFromCache();
  }

  private applyHeaderFromColumnsList(): void {
    if (!this.columnsList?.length) {
      return;
    }
    // Seed SQL cache from each action's row set
    for (const row of this.columnsList) {
      if (row.sqlquery && row.actiontype) {
        this.cacheSqlForAction(row.actiontype, row.sqlquery);
      }
    }
    const preferred = (this.uploadsConfigModel1.actiontype || 'Insert').toLowerCase();
    const header = this.columnsList.find(
      row => row.uploadtemplatename === this.uploadsConfigModel1.uploadtemplatename
        && (row.actiontype || '').toLowerCase() === preferred
    ) || this.columnsList.find(
      row => row.uploadtemplatename === this.uploadsConfigModel1.uploadtemplatename
    ) || this.columnsList[0];
    this.applyHeaderFields(header);
    this.preloadMissingSqlFromApi();
  }

  private cacheSqlForAction(actionType: string, sql: string): void {
    if (!actionType || !sql) {
      return;
    }
    if (actionType.toLowerCase() === 'update') {
      this.sqlByAction.Update = sql;
    } else {
      this.sqlByAction.Insert = sql;
    }
  }

  private getCachedSql(actionType: string): string {
    return actionType?.toLowerCase() === 'update'
      ? this.sqlByAction.Update
      : this.sqlByAction.Insert;
  }

  private syncActiveSqlFromCache(): void {
    this.activeSqlQuery = this.getCachedSql(this.uploadsConfigModel1.actiontype) || '';
    this.uploadsConfigModel1.sqlquery = this.activeSqlQuery;
  }

  private persistActiveSqlToCache(): void {
    const sql = this.activeSqlQuery || '';
    this.cacheSqlForAction(this.uploadsConfigModel1.actiontype, sql);
    this.uploadsConfigModel1.sqlquery = sql;
  }

  private preloadMissingSqlFromApi(): void {
    const recordTemplate = this.uploadsConfigModel1.recordtemplate;
    if (!recordTemplate) {
      return;
    }
    ['Insert', 'Update'].forEach(actionType => {
      if (this.getCachedSql(actionType)) {
        return;
      }
      this.subscriptions.push(
        this.uploadsConfigService.getSqlQueryForAction(recordTemplate, actionType).subscribe(
          (response: { sqlquery: string }) => {
            const sql = (response?.sqlquery || '').trim();
            if (!sql) {
              return;
            }
            this.cacheSqlForAction(actionType, sql);
            if ((this.uploadsConfigModel1.actiontype || '').toLowerCase() === actionType.toLowerCase()) {
              this.activeSqlQuery = sql;
              this.uploadsConfigModel1.sqlquery = sql;
            }
          },
          (errorResponse: HttpErrorResponse) => {
            this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
          }
        )
      );
    });
  }

  private syncServiceModel(): void {
    this.persistActiveSqlToCache();
    this.uploadsConfigService.uploadsConfigModel = this.uploadsConfigModel1;
  }

  redirect(url: string): void {
    this.router.navigate(['/' + url]);
  }

  downloadExcelHeaders(): void {
    if (!this.uploadsConfigModel1.recordtemplate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select a record template');
      return;
    }
    if (!this.uploadsConfigModel1.actiontype) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Insert or Update');
      return;
    }
    const fileTitle = this.uploadsConfigModel1.actiontype === 'Update'
      ? 'UploadsConfig_Update'
      : 'UploadsConfig_Insert';
    this.subscriptions.push(
      this.uploadsConfigService.getRecordsListForAction(
        this.uploadsConfigModel1.recordtemplate,
        this.uploadsConfigModel1.actiontype
      ).subscribe(
        (response: UploadsConfigModel[]) => {
          this.ete.exportHeaderOnlyTemplate(fileTitle, response);
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  public onActionTypeChange(actionType: string): void {
    this.persistActiveSqlToCache();
    this.uploadsConfigModel1.actiontype = actionType;
    this.syncActiveSqlFromCache();
    if (this.activeSqlQuery) {
      return;
    }
    this.loadSqlQueryForAction(actionType);
  }

  private loadSqlQueryForAction(actionType: string): void {
    const recordTemplate = this.uploadsConfigModel1.recordtemplate;
    if (!recordTemplate || !actionType) {
      return;
    }
    this.subscriptions.push(
      this.uploadsConfigService.getSqlQueryForAction(recordTemplate, actionType).subscribe(
        (response: { sqlquery: string }) => {
          if ((this.uploadsConfigModel1.actiontype || '').toLowerCase() !== actionType.toLowerCase()) {
            return;
          }
          const sql = (response?.sqlquery || '').trim();
          this.cacheSqlForAction(actionType, sql);
          this.activeSqlQuery = sql;
          this.uploadsConfigModel1.sqlquery = sql;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  public updateUploadsConfig(): void {
    this.persistActiveSqlToCache();
    if (!this.uploadsConfigModel1.uploadtemplatename) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please enter upload template name');
    } else if (!this.uploadsConfigModel1.recordtemplate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please enter record template');
    } else if (!this.uploadsConfigModel1.actiontype) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select insert/update');
    } else if (!this.activeSqlQuery) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please enter query');
    } else {
      const selectedAction = (this.uploadsConfigModel1.actiontype || '').toLowerCase();
      // Prefer existing rows for this action; otherwise send any row set as clone source
      const actionRows = this.columnsList.filter(
        row => (row.actiontype || '').toLowerCase() === selectedAction
      );
      const rowsToSave = actionRows.length > 0 ? actionRows : this.columnsList;

      this.selectedUserDoc = rowsToSave.map(row => ({
        id: row.id,
        uploadtemplatename: this.uploadsConfigModel1.uploadtemplatename,
        recordtemplate: this.uploadsConfigModel1.recordtemplate,
        uploadtemplatedesc: this.uploadsConfigModel1.uploadtemplatedesc,
        transformationroutine: this.uploadsConfigModel1.transformationroutine,
        actiontype: this.uploadsConfigModel1.actiontype,
        columnname: row.columnname,
        columntype: row.columntype,
        status: row.status,
        columnorder: row.columnorder,
        sqlquery: this.activeSqlQuery,
        created_by: this.loggedinUser
      } as UploadsConfigModel));

      this.subscriptions.push(
        this.uploadsConfigService.updateUploadsConfig(this.selectedUserDoc).subscribe(
          () => {
            this.notificationService.sendNotification(NotificationType.SUCCESS, 'Updated successfully');
            this.selectedUserDoc = [];
            this.syncServiceModel();
            this.getColumnsEditList();
          },
          (errorResponse: HttpErrorResponse) => {
            this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
          }
        )
      );
    }
  }

  public getColumnsEditList(): void {
    this.subscriptions.push(
      this.uploadsConfigService.getColumnsEditList(this.uploadsConfigModel1.recordtemplate).subscribe(
        (res: UploadsConfigModel[]) => {
          this.columnsList = (res || []).filter(
            row => row.uploadtemplatename === this.uploadsConfigModel1.uploadtemplatename
          );
          this.applyHeaderFromColumnsList();
          this.getColumnsConfigList();
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  public getColumnsConfigList(): void {
    this.subscriptions.push(
      this.uploadsConfigService.getColumnsConfigList(
        this.uploadsConfigModel1.recordtemplate,
        this.uploadsConfigModel1.uploadtemplatename
      ).subscribe(
        (res: UploadsConfigModel[]) => {
          this.columnsEditList = res;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notificationService.sendNotification(NotificationType.ERROR, extractApiErrorMessage(errorResponse));
        }
      )
    );
  }

  trackByColumnIndex(index: number): number {
    return index;
  }
}
